import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Save, RefreshCw, Eye } from "lucide-react";
import { API } from "@/lib/auth";
import RichTextEmailComposer from "@/components/admin/RichTextEmailComposer";
import EmailPreviewModal from "@/components/admin/EmailPreviewModal";

/** Combined Marketing tab: Analytics IDs + Discount codes + Newsletter + Gift vouchers overview. */
export default function MarketingEditor() {
  const [tab, setTab] = useState("analytics");
  const tabs = [
    { k: "analytics", l: "GA + Pixel" },
    { k: "codes", l: "Discount codes" },
    { k: "trade", l: "Trade discount" },
    { k: "newsletter", l: "Newsletter" },
    { k: "campaign", l: "Send campaign" },
    { k: "vouchers", l: "Gift vouchers" },
  ];
  return (
    <div className="space-y-6" data-testid="admin-marketing-panel">
      <div className="flex gap-1.5 flex-wrap">
        {tabs.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`mkt-sub-${t.k}`}
            className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest ${tab === t.k ? "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]" : "bg-white border border-[hsl(var(--onyx)/0.1)] text-[hsl(var(--onyx)/0.7)]"}`}>
            {t.l}
          </button>
        ))}
      </div>
      {tab === "analytics" && <AnalyticsSection />}
      {tab === "codes" && <DiscountCodesSection />}
      {tab === "trade" && <TradeDiscountSection />}
      {tab === "newsletter" && <NewsletterSection />}
      {tab === "campaign" && <CampaignSection />}
      {tab === "vouchers" && <VouchersSection />}
    </div>
  );
}

// ————— Trade discount — % applied automatically at checkout for approved trade accounts —————
function TradeDiscountSection() {
  const [pct, setPct] = useState(null);
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/site/content`)
      .then((r) => {
        const v = Number((r.data?.trade || {}).discount_pct);
        const safe = Number.isFinite(v) ? v : 15;
        setPct(safe);
        setInitial(safe);
      })
      .catch(() => {
        setPct(15);
        setInitial(15);
      });
  }, []);

  const save = async () => {
    const value = parseInt(pct, 10);
    if (isNaN(value) || value < 0 || value > 100) {
      toast.error("Enter a whole number between 0 and 100");
      return;
    }
    if (!window.confirm(`Set trade discount to ${value}% for all approved trade accounts, on every product?`)) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/site/content/trade`, { data: { discount_pct: value } });
      setInitial(value);
      toast.success(`Trade discount set to ${value}%`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (pct === null) return <div className="p-10 text-center text-[hsl(var(--onyx)/0.5)]">Loading…</div>;
  const dirty = Number(pct) !== Number(initial);

  return (
    <div className="space-y-5" data-testid="trade-discount-panel">
      <div className="rounded-3xl bg-blush/40 border border-[hsl(var(--gold)/0.4)] p-5 text-sm text-[hsl(var(--onyx)/0.85)] leading-relaxed">
        Approved trade accounts automatically get this % off <strong>every shop product</strong> (bundles, accessories,
        digital downloads) the moment they check out. Courses and gift vouchers are excluded.
      </div>

      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6">
        <div className="overline">Current discount</div>
        <div className="mt-4 flex items-end gap-4">
          <div className="flex items-baseline gap-2">
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={pct}
              onChange={(e) => setPct(e.target.value)}
              data-testid="trade-discount-input"
              className="w-28 h-14 rounded-2xl bg-white border border-[hsl(var(--onyx)/0.2)] px-4 font-display text-3xl outline-none focus:border-[hsl(var(--onyx))]"
            />
            <span className="font-display text-3xl text-[hsl(var(--onyx)/0.55)]">%</span>
          </div>
          <button
            onClick={save}
            disabled={!dirty || saving}
            data-testid="trade-discount-save"
            className="btn-pill btn-primary disabled:opacity-40"
          >
            {saving ? "Saving…" : dirty ? "Save" : "Saved"}
          </button>
        </div>
        <p className="text-[11px] text-[hsl(var(--onyx)/0.5)] mt-4 leading-relaxed">
          Takes effect immediately for the next checkout. Existing carts refresh their totals on refresh.
        </p>
      </div>

      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6">
        <div className="overline">What this does</div>
        <ul className="mt-3 text-sm text-[hsl(var(--onyx)/0.75)] space-y-2 list-disc pl-5">
          <li>Applied automatically at Stripe checkout — no code needed</li>
          <li>Line items are re-priced (custom price_data) so Stripe receipts show the discounted amount</li>
          <li>Order snapshot stores both list_amount and discounted amount for your records</li>
          <li>Only activates when the buyer is logged in and their user record has <code className="px-1.5 py-0.5 rounded bg-[hsl(var(--blush)/0.4)] text-[hsl(var(--onyx))]">trade_status: approved</code></li>
        </ul>
      </div>
    </div>
  );
}


// ————— Send campaign to newsletter —————
function CampaignSection() {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [list, setList] = useState("all"); // "all" | "free_education"
  const [subject, setSubject] = useState("\u2728 We're live \u2014 20% off, on us");
  const [preheader, setPreheader] = useState("20% off everything \u00b7 use LAUNCH20 at checkout \u00b7 valid to 18 Aug");
  const [excludeTest, setExcludeTest] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const defaultHtml = `<h1>Welcome to our new home online.</h1><p>Our new website is live — Nooks Pro Hair, Hair Loss Solutions, Pamper Parties, the brand-new Complete Hair Extension Guide, and everything in-between.</p><p><strong>Your launch code:</strong> LAUNCH20 — 20% off anything in the shop.</p><p><a data-cta="button" href="https://malekdridihairandbeauty.com/shop">Shop the launch</a></p><p><em>You're receiving this because you signed up for salon updates. One redemption per customer. Excludes gift vouchers.</em></p>`;
  const freeEduHtml = `<h1>This week's lesson</h1><p>A quick tip from Malek to help you become a more confident, calmer, more skilled stylist. Replace this paragraph with today's teaching.</p><p><a data-cta="button" href="https://malekdridihairandbeauty.com/free-education">Read the full lesson</a></p><p><em>You're receiving this because you joined NøoksPro Free Education. Unsubscribe anytime.</em></p>`;
  const [html, setHtml] = useState(defaultHtml);

  const loadPreview = async (l) => {
    const params = l && l !== "all" ? `?list=${encodeURIComponent(l)}` : "";
    try {
      const r = await axios.get(`${API}/admin/marketing/newsletter-preview${params}`);
      setPreview(r.data);
    } catch (e) {
      setPreview(null);
    }
  };

  useEffect(() => {
    loadPreview(list);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  const send = async () => {
    const recipients = excludeTest ? preview?.recipients_if_excluded : preview?.active;
    const listLabel = list === "free_education" ? "NøoksPro Free Education" : "the full newsletter list";
    if (!window.confirm(`Send this campaign to ${recipients} subscriber${recipients === 1 ? "" : "s"} on ${listLabel}? This cannot be undone.`)) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/admin/marketing/send-campaign`, {
        subject,
        preheader,
        html_body: html,
        exclude_test_emails: excludeTest,
        list: list === "all" ? null : list,
      });
      setResult(r.data);
      toast.success(`Sent ${r.data.sent} · Failed ${r.data.failed} · Skipped ${r.data.skipped}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  const LIST_OPTIONS = [
    { k: "all", l: "Everyone" },
    { k: "free_education", l: "NøoksPro Free Education" },
  ];

  return (
    <div className="space-y-5" data-testid="admin-campaign-panel">
      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-5">
        <div className="overline mb-3">Send to</div>
        <div className="flex flex-wrap gap-2">
          {LIST_OPTIONS.map((o) => (
            <button
              key={o.k}
              onClick={() => setList(o.k)}
              data-testid={`campaign-list-${o.k}`}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest transition-colors ${
                list === o.k
                  ? "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]"
                  : "bg-white border border-[hsl(var(--onyx)/0.1)] text-[hsl(var(--onyx)/0.7)] hover:border-[hsl(var(--onyx)/0.3)]"
              }`}
            >
              {o.l}
            </button>
          ))}
          {list === "free_education" && (
            <button
              type="button"
              onClick={() => setHtml(freeEduHtml)}
              data-testid="campaign-load-freeedu-template"
              className="px-4 py-2 rounded-full text-xs uppercase tracking-widest bg-[hsl(var(--blush)/0.6)] border border-[hsl(var(--gold)/0.4)] text-[hsl(var(--onyx))] hover:bg-[hsl(var(--blush))]"
            >
              Use Free-Education template
            </button>
          )}
        </div>
      </div>

      <div className="rounded-3xl bg-blush/40 border border-[hsl(var(--gold)/0.4)] p-5 flex items-start gap-3">
        <div className="text-sm text-[hsl(var(--onyx)/0.8)] leading-relaxed" data-testid="campaign-preview-status">
          <strong>{list === "free_education" ? "Free Education list" : "Newsletter"} status:</strong>{" "}
          {preview ? (
            <>
              {preview.active} active subscriber{preview.active === 1 ? "" : "s"} in this segment.{" "}
              {preview.test_emails > 0 && <>({preview.test_emails} look like test emails.)</>}
            </>
          ) : "Loading…"}
        </div>
      </div>

      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-4">
        <div className="overline">Compose</div>
        <label className="block">
          <span className="overline">Subject</span>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="campaign-subject"
            className="mt-1.5 w-full h-11 rounded-full bg-white border border-black/10 px-4 text-sm outline-none focus:border-[hsl(var(--burgundy))]" />
        </label>
        <label className="block">
          <span className="overline">Preheader (preview text)</span>
          <input value={preheader} onChange={(e) => setPreheader(e.target.value)}
            className="mt-1.5 w-full h-11 rounded-full bg-white border border-black/10 px-4 text-sm outline-none focus:border-[hsl(var(--burgundy))]" />
        </label>
        <label className="block">
          <span className="overline">Email body</span>
          <div className="mt-1.5">
            <RichTextEmailComposer
              value={html}
              onChange={setHtml}
              placeholder="Type your email here — bold, italic, headings, lists, links, buttons and emojis. Paste from ChatGPT works too."
            />
          </div>
        </label>
        <label className="flex items-center gap-2 text-sm text-[hsl(var(--onyx)/0.75)]">
          <input type="checkbox" checked={excludeTest} onChange={(e) => setExcludeTest(e.target.checked)}
            className="w-4 h-4 rounded border-[hsl(var(--onyx)/0.3)] text-[hsl(var(--burgundy))]" />
          Exclude test emails (@example.com, test-*, screenshot-test-*, quiz*, pw_test_*)
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={() => setShowPreview(true)}
          disabled={!subject.trim() || !html.trim()}
          data-testid="campaign-preview-btn"
          className="btn-pill btn-outline !text-xs disabled:opacity-40"
        >
          <Eye size={14} /> Preview email
        </button>
        <button
          onClick={send}
          disabled={busy || !preview}
          data-testid="campaign-send-btn"
          className="btn-pill btn-primary !text-xs disabled:opacity-50"
        >
          {busy ? "Sending…" : `Send to ${excludeTest ? (preview?.recipients_if_excluded ?? "?") : (preview?.active ?? "?")} subscriber${(excludeTest ? preview?.recipients_if_excluded : preview?.active) === 1 ? "" : "s"}`}
        </button>
        <button onClick={() => setHtml(defaultHtml)} className="text-xs text-[hsl(var(--onyx)/0.5)] hover:text-[hsl(var(--onyx))] underline">
          Reset to launch template
        </button>
      </div>

      <EmailPreviewModal
        open={showPreview}
        onClose={() => setShowPreview(false)}
        subject={subject}
        preheader={preheader}
        htmlBody={html}
      />

      {result && (
        <div className="rounded-3xl bg-green-50 border border-green-200 p-5 text-sm" data-testid="campaign-result">
          <div className="overline text-green-700">Campaign sent</div>
          <div className="mt-2 grid gap-1">
            <div>✅ Sent: <strong>{result.sent}</strong></div>
            <div>❌ Failed: <strong>{result.failed}</strong></div>
            <div>⏭ Skipped (test / invalid): <strong>{result.skipped}</strong></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ————— Analytics IDs —————
function AnalyticsSection() {
  const [ga, setGa] = useState("");
  const [pix, setPix] = useState("");
  const [orig, setOrig] = useState({ ga: "", pix: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/site/content`).then((r) => {
      const a = r.data?.analytics || {};
      setGa(a.ga4_id || ""); setPix(a.meta_pixel_id || "");
      setOrig({ ga: a.ga4_id || "", pix: a.meta_pixel_id || "" });
    });
  }, []);
  const dirty = ga !== orig.ga || pix !== orig.pix;
  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/site/content/analytics`, {
        data: { ga4_id: ga.trim(), meta_pixel_id: pix.trim() },
      });
      toast.success("Analytics IDs saved — scripts load on next page view (after cookie consent).");
      setOrig({ ga: ga.trim(), pix: pix.trim() });
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-4">
      <div className="overline">Google Analytics + Meta Pixel</div>
      <p className="text-xs text-[hsl(var(--onyx)/0.6)] max-w-lg">
        Only IDs are needed — the scripts inject themselves once a visitor accepts the cookie banner. Tracked events: page_view, view_item, add_to_cart, begin_checkout, purchase, newsletter_signup.
      </p>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">GA4 Measurement ID</label>
        <input value={ga} onChange={(e) => setGa(e.target.value)} placeholder="G-XXXXXXXXXX" data-testid="ga4-id"
          className="w-full h-11 mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm font-mono outline-none focus:border-[hsl(var(--onyx))]" />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Meta Pixel ID</label>
        <input value={pix} onChange={(e) => setPix(e.target.value)} placeholder="123456789012345" data-testid="pixel-id"
          className="w-full h-11 mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm font-mono outline-none focus:border-[hsl(var(--onyx))]" />
      </div>
      <button onClick={save} disabled={!dirty || saving} data-testid="analytics-save" className="btn-pill btn-primary disabled:opacity-50">
        <Save size={14} /> {saving ? "Saving…" : dirty ? "Save" : "Saved"}
      </button>
    </div>
  );
}

// ————— Discount codes —————
function DiscountCodesSection() {
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState({ code: "", kind: "percent", value: 10, min_spend: 0, applies_to: "both", max_uses: "", expires_at: "", active: true });
  const [busy, setBusy] = useState(false);
  const load = async () => setCodes((await axios.get(`${API}/admin/discount-codes`)).data);
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!/^[A-Za-z0-9_-]+$/.test(form.code)) return toast.error("Code must be alphanumeric/-/_ only");
    setBusy(true);
    try {
      await axios.post(`${API}/admin/discount-codes`, {
        ...form,
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        expires_at: form.expires_at || null,
      });
      toast.success(`Code ${form.code.toUpperCase()} created`);
      setForm({ code: "", kind: "percent", value: 10, min_spend: 0, applies_to: "both", max_uses: "", expires_at: "", active: true });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create code"); }
    finally { setBusy(false); }
  };
  const toggle = async (code, active) => { await axios.patch(`${API}/admin/discount-codes/${code}`, { active }); load(); };
  const remove = async (code) => { if (!window.confirm(`Delete ${code}?`)) return; await axios.delete(`${API}/admin/discount-codes/${code}`); load(); };
  return (
    <div className="grid gap-6 lg:grid-cols-12">
      <form onSubmit={submit} className="lg:col-span-5 rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-3 h-fit sticky top-28" data-testid="discount-create-form">
        <div className="overline">Create discount code</div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Code (e.g. WELCOME10)</label>
          <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="dc-code"
            className="w-full h-11 mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm font-mono outline-none focus:border-[hsl(var(--onyx))]" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Type</label>
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="dc-kind"
              className="w-full h-11 mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-3 text-sm bg-white">
              <option value="percent">Percentage %</option>
              <option value="fixed">Fixed £ off</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Value ({form.kind === "percent" ? "%" : "£"})</label>
            <input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: Number(e.target.value) })} data-testid="dc-value" min="1"
              className="w-full h-11 mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Min spend £</label>
            <input type="number" value={form.min_spend} onChange={(e) => setForm({ ...form, min_spend: Number(e.target.value) })}
              className="w-full h-11 mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Applies to</label>
            <select value={form.applies_to} onChange={(e) => setForm({ ...form, applies_to: e.target.value })}
              className="w-full h-11 mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-3 text-sm bg-white">
              <option value="both">Everything</option>
              <option value="shop">Shop only</option>
              <option value="academy">Academy only</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Max uses (blank = ∞)</label>
            <input type="number" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
              className="w-full h-11 mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Expires (optional)</label>
            <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
              className="w-full h-11 mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm" />
          </div>
        </div>
        <button type="submit" disabled={busy} data-testid="dc-submit" className="btn-pill btn-primary w-full disabled:opacity-60">
          <Plus size={14} /> {busy ? "Creating…" : "Create code"}
        </button>
      </form>
      <div className="lg:col-span-7 space-y-2">
        {codes.length === 0 ? (
          <div className="rounded-3xl bg-white border border-dashed border-[hsl(var(--onyx)/0.15)] p-10 text-center text-[hsl(var(--onyx)/0.5)]">No codes yet.</div>
        ) : codes.map((c) => (
          <div key={c.code} data-testid={`dc-row-${c.code}`} className="rounded-2xl bg-white border border-[hsl(var(--onyx)/0.06)] p-4 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[160px]">
              <div className="font-mono text-lg">{c.code}</div>
              <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">
                {c.kind === "percent" ? `${c.value}% off` : `£${c.value} off`} · {c.applies_to} · {c.uses || 0}{c.max_uses ? `/${c.max_uses}` : ""} uses
                {c.expires_at ? ` · expires ${c.expires_at}` : ""}
              </div>
            </div>
            <button onClick={() => toggle(c.code, !c.active)} className="btn-pill btn-outline !text-xs !py-1.5">
              {c.active ? "Disable" : "Enable"}
            </button>
            <button onClick={() => remove(c.code)} className="btn-pill btn-outline !text-xs !py-1.5" data-testid={`dc-delete-${c.code}`}>
              <Trash2 size={12} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ————— Newsletter subscribers —————
function NewsletterSection() {
  const [data, setData] = useState(null);
  const [list, setList] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = async (l) => {
    setLoading(true);
    try {
      const params = l && l !== "all" ? `?list=${encodeURIComponent(l)}` : "";
      const r = await axios.get(`${API}/admin/newsletter${params}`);
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load subscribers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(list);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  if (!data && loading) return <div className="p-10 text-center text-[hsl(var(--onyx)/0.5)]">Loading…</div>;
  if (!data) return <div className="p-10 text-center text-[hsl(var(--onyx)/0.5)]">Loading…</div>;

  const exportCsv = () => {
    const rows = ["Email,Name,Source,Subscribed at,Status,Welcome sent",
      ...data.subscribers.map((s) => {
        const src = (s.sources && s.sources.length ? s.sources.join("|") : s.source) || "";
        const welcomed = s.free_education_welcome_sent_at ? s.free_education_welcome_sent_at.slice(0, 10) : "";
        return `${s.email},"${(s.name || "").replace(/"/g, "'")}",${src},${s.subscribed_at || ""},${s.unsubscribed ? "unsubscribed" : "active"},${welcomed}`;
      })];
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `newsletter-subscribers-${list}.csv`;
    link.click();
  };

  const LIST_OPTIONS = [
    { k: "all", l: "All lists" },
    { k: "free_education", l: "Free Education" },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2" data-testid="newsletter-list-tabs">
        {LIST_OPTIONS.map((o) => (
          <button
            key={o.k}
            onClick={() => setList(o.k)}
            data-testid={`newsletter-list-${o.k}`}
            className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest transition-colors ${
              list === o.k
                ? "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]"
                : "bg-white border border-[hsl(var(--onyx)/0.1)] text-[hsl(var(--onyx)/0.7)] hover:border-[hsl(var(--onyx)/0.3)]"
            }`}
          >
            {o.l}
          </button>
        ))}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6">
          <div className="overline">Active subscribers{list !== "all" ? ` (${list.replace("_", " ")})` : ""}</div>
          <div className="font-display text-5xl mt-3" data-testid="newsletter-active-count">{data.total_active}</div>
        </div>
        <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6">
          <div className="overline">Unsubscribed</div>
          <div className="font-display text-5xl mt-3">{data.total_unsubscribed}</div>
        </div>
      </div>

      {data.sources_breakdown && data.sources_breakdown.length > 0 && (
        <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6">
          <div className="overline mb-3">Sources breakdown</div>
          <div className="flex flex-wrap gap-2">
            {data.sources_breakdown.map((s) => (
              <span
                key={s.source}
                data-testid={`source-pill-${s.source}`}
                className={`px-3 py-1.5 rounded-full text-xs ${
                  s.source === "free_education"
                    ? "bg-[hsl(var(--blush)/0.6)] text-[hsl(var(--onyx))] border border-[hsl(var(--gold)/0.4)]"
                    : "bg-[hsl(var(--onyx)/0.06)] text-[hsl(var(--onyx)/0.75)]"
                }`}
              >
                {s.source || "unknown"} · <strong>{s.count}</strong>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="overline">Subscribers</div>
          <button onClick={exportCsv} className="btn-pill btn-outline !text-xs" data-testid="newsletter-export">Export CSV</button>
        </div>
        <div className="max-h-[600px] overflow-y-auto text-sm space-y-1" data-testid="newsletter-subscribers-list">
          {data.subscribers.length === 0 ? (
            <div className="py-10 text-center text-[hsl(var(--onyx)/0.5)]">No subscribers in this segment yet.</div>
          ) : data.subscribers.map((s, i) => {
            const srcs = (s.sources && s.sources.length ? s.sources : [s.source]).filter(Boolean);
            const isFreeEdu = srcs.includes("free_education");
            return (
              <div key={i} className={`flex items-center gap-3 py-2 border-b border-[hsl(var(--onyx)/0.05)] ${s.unsubscribed ? "opacity-50 line-through" : ""}`}>
                <span className="flex-1 truncate">
                  {s.email}
                  {s.name ? <span className="text-[hsl(var(--onyx)/0.5)] ml-2">· {s.name}</span> : null}
                </span>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${
                  isFreeEdu
                    ? "bg-[hsl(var(--blush)/0.6)] text-[hsl(var(--onyx))]"
                    : "text-[hsl(var(--onyx)/0.5)]"
                }`}>
                  {srcs.join(" · ") || "footer"}
                </span>
                <span className="text-xs text-[hsl(var(--onyx)/0.5)]">{s.subscribed_at?.slice(0, 10)}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ————— Gift vouchers listing —————
function VouchersSection() {
  const [vouchers, setVouchers] = useState([]);
  useEffect(() => { axios.get(`${API}/admin/gift-vouchers`).then((r) => setVouchers(r.data || [])); }, []);
  return (
    <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6">
      <div className="overline mb-3">Issued gift vouchers</div>
      {vouchers.length === 0 ? (
        <div className="text-center text-[hsl(var(--onyx)/0.5)] py-8">No gift vouchers issued yet.</div>
      ) : (
        <div className="space-y-2">
          {vouchers.map((v) => (
            <div key={v.code} className="rounded-2xl border border-[hsl(var(--onyx)/0.06)] p-4 flex flex-wrap gap-3 items-center">
              <div className="flex-1 min-w-[240px]">
                <div className="font-mono text-sm">{v.code}</div>
                <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.5)] mt-1">
                  From {v.buyer_email} → {v.recipient_email || "(to buyer)"} · created {v.created_at?.slice(0, 10)}
                </div>
              </div>
              <div className="text-right">
                <div className="font-display text-xl">£{((v.remaining_amount_pence || 0) / 100).toFixed(2)}</div>
                <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">left of £{((v.initial_amount_pence || 0) / 100).toFixed(2)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
